import { useEffect, useMemo, useState } from "react";
import type { BlurEvent } from "react-native";
import { TextInput, type TextInputProps } from "~/components/form/TextInput";
import { nullish } from "~/util";
import {
    DEFAULT_NUMBER_FORMAT,
    formatNumeric,
    INTEGER_NUMBER_FORMAT,
    type NumberFormatOptions,
    type Numeric,
    type NumericOrEmptyString,
    type NumericString,
    type OptionalNumeric,
} from "~/util/string";

type ResponseType = NumericString | null;
type FilledSetter = (text: NumericString) => void;
type OptionalSetter = (text: ResponseType) => void;
export type NumericSetter = FilledSetter | OptionalSetter;

type ResolvedSetter<TEmpty extends OptionalNumeric> = TEmpty extends Numeric ? FilledSetter : OptionalSetter;
type ResolvedResponse<TEmpty extends OptionalNumeric> = TEmpty extends Numeric ? NumericString : ResponseType;

export type NumberInputProps<TEmpty extends OptionalNumeric = OptionalNumeric> = {
    value?: OptionalNumeric;
    min?: OptionalNumeric;
    max?: OptionalNumeric;
    asInt?: boolean;
    empty?: TEmpty;
    onChangeText?: ResolvedSetter<TEmpty>;
    readonly format?: NumberFormatOptions;
} & Omit<TextInputProps, "value" | "inputMode" | "keyboardType" | "onChangeText">;

export const NumberInput = <TEmpty extends OptionalNumeric = OptionalNumeric>({
    onBlur,
    onChangeText,
    value,
    min,
    max,
    asInt,
    format,
    empty,
    ...inputProps
}: NumberInputProps<TEmpty>) => {
    const formatter = useMemo(
        () => ({
            ...(format ?? DEFAULT_NUMBER_FORMAT),
            ...(asInt && INTEGER_NUMBER_FORMAT),
        }),
        [format, asInt],
    );

    const input = useMemo(() => formatNumeric(value ?? empty, formatter) ?? "", [value, empty, formatter]);
    const [number, setNumber] = useState<NumericOrEmptyString>(input);

    const setValue = (value: NumericOrEmptyString, with_formatting = false) => {
        const fallback = (nullish(empty) ? null : `${empty}`) as ResolvedResponse<TEmpty>;
        const formatted = formatNumeric(value, formatter, min, max) ?? fallback;

        setNumber((number) => {
            const compare = formatNumeric(number, formatter, min, max) ?? fallback;

            return with_formatting || compare !== formatted ? (formatted ?? value) : value;
        });

        // @ts-expect-error
        (formatted !== input || with_formatting) && onChangeText?.(formatted);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(setValue): setter
    useEffect(() => {
        setValue(input);
    }, [input]);

    const handleBlur = (event: BlurEvent) => {
        setValue(number, true);
        onBlur?.(event);
    };

    return (
        <TextInput
            {...inputProps}
            inputMode="decimal"
            keyboardType="numeric"
            value={number}
            onChangeText={setValue as (text: string) => void}
            onBlur={handleBlur}
        />
    );
};
